import type {CommentBoxConfig, Subject} from '@github-ui/comment-box'
import {getQuotedText} from '@github-ui/commenting'
import type {ReactionViewerUser$key} from '@github-ui/reaction-viewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useToastContext} from '@github-ui/toast'
import {useSessionStorage} from '@github-ui/use-safe-storage'
import {Box} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, type PreloadedQuery, useRelayEnvironment} from 'react-relay'
import {useFragment, usePreloadedQuery} from 'react-relay/hooks'

import type {IssueBody$key} from './__generated__/IssueBody.graphql'
import type {IssueBodyContent$key} from './__generated__/IssueBodyContent.graphql'
import type {IssueBodyQuery} from './__generated__/IssueBodyQuery.graphql'
import {IssueBodyEditor} from './components/IssueBodyEditor'
import {IssueBodyHeader} from './components/IssueBodyHeader'
import type {IssueBodyHeaderActionsProps} from './components/IssueBodyHeaderActions'
import {IssueBodyViewer} from './components/IssueBodyViewer'
import {ERRORS, LABELS, TEST_IDS, VALUES} from './constants'
import {useTaskListBlock} from './hooks/useTaskListBlock'
import type {updateIssueBodyMutation$data} from './mutations/__generated__/updateIssueBodyMutation.graphql'
import {commitUpdateIssueBodyMutation} from './mutations/update-issue-body-mutation'

export type {IssueBody$data} from './__generated__/IssueBody.graphql'
export type {IssueBodyContent$data} from './__generated__/IssueBodyContent.graphql'
export type {IssueBodyQuery} from './__generated__/IssueBodyQuery.graphql'
export type {IssueBodyHeaderAuthor$data} from './components/__generated__/IssueBodyHeaderAuthor.graphql'
export {IssueBodyEditor} from './components/IssueBodyEditor'
export {IssueBodyHeader} from './components/IssueBodyHeader'
export type {IssueBodyHeaderActionsProps} from './components/IssueBodyHeaderActions'
export {IssueBodyLoading} from './components/IssueBodyLoading'
export {IssueBodyViewer} from './components/IssueBodyViewer'
export {TEST_IDS} from './constants'

export const IssueBodyGraphqlQuery = graphql`
  query IssueBodyQuery($owner: String!, $repo: String!, $number: Int!) {
    repository(owner: $owner, name: $repo) {
      issue(number: $number) {
        ...IssueBody
        # eslint-disable-next-line relay/must-colocate-fragment-spreads TODO: fix this
        ...IssueActions
      }
    }
    viewer {
      # eslint-disable-next-line relay/must-colocate-fragment-spreads
      ...ReactionViewerUser
    }
  }
`

export const issueBodyContentFragment = graphql`
  fragment IssueBodyContent on Issue {
    body
    bodyHTML(unfurlReferences: true, renderTasklistBlocks: true)
    bodyVersion
  }
`

type IssueBodyProps = {
  issuesQueryRef: PreloadedQuery<IssueBodyQuery>
  onLinkClick?: (event: MouseEvent) => void
  onIssueUpdate?: () => void
  commentBoxConfig?: CommentBoxConfig
  onIssueEditStateChange?: (isEditing: boolean) => void
  isIssueEditActive?: () => boolean
  onCommentReply: (quotedComment: string) => void
  highlightedEventText?: string
}

type IssueBodyInternalProps = Omit<IssueBodyProps, 'issuesQueryRef'> & {
  issue: IssueBody$key
  viewer: ReactionViewerUser$key
}

export function IssueBody({issuesQueryRef, ...props}: IssueBodyProps) {
  const preloadedData = usePreloadedQuery<IssueBodyQuery>(IssueBodyGraphqlQuery, issuesQueryRef)

  return (
    preloadedData.repository &&
    preloadedData.repository.issue && (
      <IssueBodyInternal issue={preloadedData.repository.issue} viewer={preloadedData.viewer} {...props} />
    )
  )
}

export function IssueBodyInternal({
  issue,
  onLinkClick,
  viewer,
  commentBoxConfig,
  onIssueEditStateChange,
  onIssueUpdate,
  isIssueEditActive,
  onCommentReply,
  highlightedEventText,
}: IssueBodyInternalProps) {
  const data = useFragment(
    graphql`
      fragment IssueBody on Issue {
        id
        databaseId
        createdAt
        viewerDidAuthor
        author {
          ...IssueBodyHeaderAuthor
        }
        repository {
          databaseId
          nameWithOwner
          slashCommandsEnabled
          id
          owner {
            login
            url
          }
        }
        url
        viewerCanUpdateNext
        viewerCanReport
        viewerCanReportToMaintainer
        locked
        # eslint-disable-next-line relay/must-colocate-fragment-spreads TODO: fix this
        ...ReactionViewerGroups
        ...IssueBodyContent
        # eslint-disable-next-line relay/must-colocate-fragment-spreads
        ...MarkdownEditHistoryViewer_comment
      }
    `,
    issue,
  )
  const {body: issueBody, bodyHTML, bodyVersion} = useFragment(issueBodyContentFragment, data as IssueBodyContent$key)
  const issueId = data.id
  const repositoryDatabaseId = data.repository.databaseId!

  const [presavedBody, setPresavedBody] = useSessionStorage<string | undefined>(
    VALUES.localStorageKeys.issueNewBody('hyperlist', issueId),
    undefined,
  )

  const [currentIssueBodyVersion, setCurrentIssueBodyVersion] = useState<string>(bodyVersion)
  const [bodyIsStale, setBodyIsStale] = useState(false)
  const [markdownBody, setMarkdownBody] = useState<string>(presavedBody || issueBody)
  const [isIssueBodyEditActive, setIsIssueBodyEditActive] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [defferedIsEditing, setDefferedIsEditing] = useState(false)
  const relayEnvironment = useRelayEnvironment()

  const issueBodyRef = useRef<HTMLDivElement>(null)

  const {addToast} = useToastContext()

  const onBodyChange = useCallback(
    (newMarkdown: string) => {
      setMarkdownBody(newMarkdown)
      setPresavedBody(newMarkdown)
      onIssueEditStateChange?.(true)
    },
    [onIssueEditStateChange, setPresavedBody],
  )

  useEffect(() => {
    // If the body version changes **after** the user started editing, keep using the previous body version
    if (defferedIsEditing) {
      return
    }
    // If the body version changes **before** the user started editing, update the current body version
    if (bodyVersion !== currentIssueBodyVersion) {
      setCurrentIssueBodyVersion(bodyVersion)
    }
  }, [bodyVersion, currentIssueBodyVersion, defferedIsEditing])

  useEffect(() => {
    if (!isIssueEditActive?.()) {
      setIsIssueBodyEditActive(false)
    }
  }, [isIssueEditActive, setIsIssueBodyEditActive])

  const subject = useMemo<Subject>(() => {
    return {
      type: 'issue',
      id: {
        id: data.id,
        databaseId: data.databaseId!,
      },
      repository: {
        databaseId: repositoryDatabaseId,
        nwo: data.repository.nameWithOwner,
        slashCommandsEnabled: data.repository.slashCommandsEnabled,
      },
    }
  }, [
    data.databaseId,
    data.id,
    data.repository.nameWithOwner,
    data.repository.slashCommandsEnabled,
    repositoryDatabaseId,
  ])

  const {viewerRef, onStartEdit, snapshot, isTasklistDirty} = useTaskListBlock({
    id: issueId,
    markdown: markdownBody,
    setMarkdown: setMarkdownBody,
    html: bodyHTML,
    isEditing: defferedIsEditing,
    bodyVersion: currentIssueBodyVersion,
  })

  useEffect(() => {
    if (isSubmitting) return

    if (isIssueBodyEditActive || isTasklistDirty) {
      return
    }

    setMarkdownBody(issueBody)

    return () => {
      setBodyIsStale(false)
      onIssueEditStateChange?.(false)
    }
  }, [isIssueBodyEditActive, isSubmitting, isTasklistDirty, issueBody, onIssueEditStateChange])

  useEffect(() => setIsIssueBodyEditActive(false), [issueId, setIsIssueBodyEditActive])

  useEffect(() => {
    setDefferedIsEditing(isIssueBodyEditActive)
    if (isIssueBodyEditActive) {
      onStartEdit()
    }
  }, [isIssueBodyEditActive, onStartEdit])

  const commitIssueBodyEdit = useCallback(() => {
    commitUpdateIssueBodyMutation({
      environment: relayEnvironment,
      input: {issueId, body: presavedBody ?? markdownBody, bodyVersion: currentIssueBodyVersion},
      onError: (error: Error) => {
        addToast({
          type: 'error',
          message: error.message.startsWith(LABELS.staleBodyErrorPrefix)
            ? ERRORS.couldNotUpdateIssueBodyStale
            : ERRORS.couldNotUpdateIssueBody,
        })

        if (error.message.startsWith(LABELS.staleBodyErrorPrefix)) {
          setIsIssueBodyEditActive(true)
          setIsSubmitting(false)
        } else {
          onIssueEditStateChange?.(false)
        }
      },
      onCompleted: (response: updateIssueBodyMutation$data) => {
        onIssueUpdate?.()
        setIsIssueBodyEditActive(false)
        onIssueEditStateChange?.(false)
        setIsSubmitting(false)
        if (response.updateIssue?.issue) {
          setCurrentIssueBodyVersion(response.updateIssue.issue.bodyVersion)
        }
      },
    })
  }, [
    relayEnvironment,
    issueId,
    presavedBody,
    markdownBody,
    currentIssueBodyVersion,
    addToast,
    onIssueEditStateChange,
    onIssueUpdate,
  ])

  const handleOnReply = (quotedText?: string) => {
    onCommentReply(quotedText || getQuotedText(issueBody))
  }

  const displayHtml = snapshot.length > 0 ? snapshot : bodyHTML

  const highlighted = useMemo(() => {
    if (!highlightedEventText) return false
    return highlightedEventText === `#issue-${data.databaseId}`
  }, [highlightedEventText, data.databaseId])

  const highlightedStyling = useMemo(
    () =>
      highlighted
        ? {
            border: '1px solid',
            borderColor: 'accent.fg',
            boxShadow: `0px 0px 0px 1px var(--fgColor-accent, var(--color-accent-fg)), 0px 0px 0px 4px var(--bgColor-accent-muted, var(--color-accent-subtle))`,
          }
        : {},
    [highlighted],
  )

  const actionProps: IssueBodyHeaderActionsProps = {
    viewerCanUpdate: data.viewerCanUpdateNext || false,
    startIssueBodyEdit: () => {
      setIsIssueBodyEditActive(true)
    },
    url: `${data.url}#issue-${data.databaseId}`,
    issueBodyRef,
    onReplySelect: handleOnReply,
    viewerCanReport: data.viewerCanReport,
    viewerCanReportToMaintainer: data.viewerCanReportToMaintainer,
    issueId: data.id,
    owner: data.repository.owner.login,
    ownerUrl: data.repository.owner.url,
    author: data.author,
  }
  return (
    <Box
      ref={viewerRef}
      sx={{
        flexGrow: 1,
        order: [1, 1, 1, 1, 0],
        ...highlightedStyling,
      }}
      data-testid={TEST_IDS.issueBody}
      className={'react-issue-body'}
      data-hpc
    >
      <h2 className="sr-only">{LABELS.issueBodyHeader}</h2>
      {defferedIsEditing || isSubmitting ? (
        <IssueBodyEditor
          editorDisabled={isSubmitting}
          trailingIcon={!isSubmitting}
          subjectId={issueId}
          subject={subject}
          body={presavedBody || markdownBody}
          bodyIsStale={bodyIsStale}
          onChange={onBodyChange}
          onCancel={() => {
            setIsIssueBodyEditActive(false)
            setMarkdownBody(issueBody)
            setPresavedBody(undefined)
            onIssueEditStateChange?.(false)
            setCurrentIssueBodyVersion(bodyVersion)
          }}
          onCommit={() => {
            setIsSubmitting(true)
            commitIssueBodyEdit()
            setPresavedBody(undefined)
          }}
          commentBoxConfig={commentBoxConfig}
        />
      ) : (
        <Box sx={{display: 'flex', flexDirection: 'row', gap: 2}}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: data.viewerDidAuthor ? 'accent.muted' : 'border.muted',
              boxShadow: 'shadow.small',
              borderRadius: 2,
              flexGrow: 1,
              width: '100%',
              minWidth: 0,
            }}
          >
            <IssueBodyHeader
              author={data.author}
              createdAt={data.createdAt}
              editHistory={data}
              actionProps={actionProps}
              viewerDidAuthor={data.viewerDidAuthor}
            />
            <IssueBodyViewer
              id={issueId}
              viewerRef={viewerRef}
              html={displayHtml as SafeHTMLString}
              markdown={issueBody}
              viewerCanUpdate={data.viewerCanUpdateNext || false}
              reactionGroups={data}
              viewer={viewer}
              onLinkClick={onLinkClick}
              locked={data.locked}
              issueBodyRef={issueBodyRef}
              bodyVersion={currentIssueBodyVersion}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

try{ IssueBody.displayName ||= 'IssueBody' } catch {}
try{ IssueBodyInternal.displayName ||= 'IssueBodyInternal' } catch {}